import React, {useMemo, useRef, useState} from 'react';
import Modal from 'react-bootstrap/Modal';
import {Form} from 'react-bootstrap';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import moment from 'moment';
import {ActionWithProgress} from '../../../system/buttons';
import PulsoidClient, {
  ValidateCouponResponse,
  CouponReason,
  RedeemCouponResponse,
} from '../../../PulsoidClient';
import css from './RedeemCouponModal.module.css';

const validationReasonToMessage: {
  [key in CouponReason]:
    | string
    | ((...params: (string | number | undefined)[]) => string);
} = {
  TrialExtended: (...params) =>
    `Almost done. You will get additional ${params[0]} days of the BRO plan trial with this coupon to elevate your content`,
  SubscriptionExtended:
    'Almost done. Your next payment date will be changed.\nThank you for your support, BRO!',
  TrialApplied: (...params) =>
    `Almost done. This code will grant you ${params[0]} days of access to the full power of Pulsoid BRO Plan to boost your content`,
  CouponNotApplicableForLifetime:
    'Wow, looks like you have a lifetime of the BRO plan already.\nThank you for your support!',
  CouponNotFound:
    'Oh no… We can’t find this coupon. \nNo worries, you can {contactUs} to get help',
  CouponAlreadyUsedByProfile:
    'Oh no… Looks like you have already redeemed this coupon',
  CouponNotApplicableForThisProfile:
    'Oh no… This coupon can’t be used for your account.\n{contactUs} if you need help',
  CouponExhaustedOrExpired:
    'Oh no… It looks like this coupon is not valid anymore',
  Unknown: 'Oh no… Something went wrong.\nPlease try again later',
};

const redeemReasonToMessage: Partial<{
  [key in CouponReason]: string;
}> = {
  TrialExtended: 'Done. Your BRO plan trial was extended.\nHave fun!',
  SubscriptionExtended:
    'Done. Your next payment date has changed now.\nThank you for your support, BRO!',
  TrialApplied:
    'Done. You have access to all power of the BRO plan now.\nHave fun!',
  CouponNotApplicableForLifetime:
    'Coupon can’t be redeemed with an active lifetime.\nThank you for your support, BRO!',
  Unknown: 'Oh no… Something went wrong.\nPlease try again later',
};

const interpolate = (str: string, params: {[key: string]: React.ReactNode}) => {
  const components = str.split(/({[^}]+})/g);
  return components.map((component, index) => {
    if (component.startsWith('{') && component.endsWith('}')) {
      const key = component.slice(1, -1);
      return React.cloneElement(params[key] as React.ReactElement, {
        key: index,
      });
    }
    return component;
  });
};

const isSuccessReason = (reason: CouponReason) => {
  return [
    'TrialExtended',
    'SubscriptionExtended',
    'TrialApplied',
    'CouponNotApplicableForLifetime',
  ].includes(reason);
};

export const RedeemCouponModal = ({show, onClose, onCouponApplied}) => {
  const [validationResponse, setValidationResponse] =
    useState<ValidateCouponResponse>();
  const [redeemResponse, setRedeemResponse] = useState<RedeemCouponResponse>();

  const [isValidating, setIsValidating] = useState(false);
  let [coupon, setCoupon] = useState(
    new URLSearchParams(window?.location?.search).get('coupon') || ''
  );

  const validationReasonMessage = useMemo(() => {
    const contactUsLink = (
      <a
        href={'https://pulsoid.net/contact'}
        style={{color: '#04bbaf'}}
        target="_blank"
        rel="noreferrer"
      >
        Contact us
      </a>
    );

    if (validationResponse?.reason) {
      let message = validationReasonToMessage[validationResponse.reason];

      if (typeof message === 'function') {
        message = message(
          moment.duration(validationResponse.duration).asDays()
        );
      }

      return interpolate(message, {contactUs: contactUsLink});
    }
  }, [validationResponse]);

  const redeemReasonMessage = useMemo(() => {
    if (redeemResponse?.reason) {
      return redeemReasonToMessage[redeemResponse.reason];
    }
  }, [redeemResponse]);

  const redeemCoupon = async () => {
    try {
      const response = await new PulsoidClient().redeemCoupon(coupon);
      await onCouponApplied();
      setRedeemResponse(response);

      const successfullRedeem = [
        'TrialExtended',
        'SubscriptionExtended',
        'TrialApplied',
        'CouponNotApplicableForLifetime',
      ].includes(response.reason);

      if (successfullRedeem) {
        setTimeout(onClose, 5000);
      }
    } catch (error) {
      console.error(error);
      setRedeemResponse({applied: false, reason: 'Unknown'});
    }
  };

  const validateCoupon = async () => {
    setIsValidating(true);
    try {
      const response = await new PulsoidClient().validateCoupon(coupon);
      setValidationResponse(response);
    } catch (error) {
      console.error(error);
      setValidationResponse({applicable: false, reason: 'Unknown'});
    }
    setIsValidating(false);
  };

  return (
    <Modal
      show={show}
      onHide={onClose}
      aria-labelledby="example-modal-sizes-title-lg"
    >
      <Modal.Header closeButton>
        <Modal.Title id="example-modal-sizes-title-lg">
          Redeem coupon
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form
          onSubmit={(e) => {
            e.preventDefault();
            validateCoupon();
          }}
        >
          <Form.Group as={Row}>
            <Form.Label column>Coupon</Form.Label>
            <Col md="6">
              <Form.Control
                type={'input'}
                value={coupon}
                onChange={(e) => setCoupon(e.target.value)}
                disabled={redeemResponse?.applied}
              />
            </Col>
            <Col md="auto">
              <ActionWithProgress
                type="submit"
                outline
                loading={isValidating}
                disabled={redeemResponse?.applied}
              >
                Validate
              </ActionWithProgress>
            </Col>
          </Form.Group>
        </Form>

        {validationResponse?.reason && !redeemResponse?.reason ? (
          <div
            className={css.feedbackMessageContainer}
            style={{
              color: isSuccessReason(validationResponse?.reason)
                ? '#212529'
                : '#fdb008',
            }}
          >
            {validationReasonMessage}
          </div>
        ) : null}

        {redeemResponse?.reason ? (
          <div
            className={css.feedbackMessageContainer}
            style={{
              color: isSuccessReason(redeemResponse?.reason)
                ? '#04bbaf'
                : '#fdb008',
            }}
          >
            {redeemReasonMessage}
          </div>
        ) : null}

        {validationResponse?.applicable && !redeemResponse ? (
          <Row className={css.redeemContainer}>
            <Col></Col>
            <Col md={'auto'}>
              <ActionWithProgress onClick={redeemCoupon}>
                Redeem
              </ActionWithProgress>
            </Col>
            <Col></Col>
          </Row>
        ) : null}
      </Modal.Body>
    </Modal>
  );
};
